import io from "socket.io-client";
import { API_BASE_URL } from "../config/apiConfig";
import { getAuthToken } from "../utils/apiUtils";

/**
 * 🔥 REAL-TIME MESSAGING SOCKET SERVICE
 *
 * Features:
 * - Real-time message delivery
 * - Typing indicators
 * - Read receipts
 * - Message delivery confirmations
 * - Auto-reconnection
 * - Event-driven architecture
 */

class MessagingSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.isConnecting = false; // Prevent duplicate connection attempts
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.activeConversations = new Set();
    this.heartbeatInterval = null;
    this.lastMessageTimestamp = localStorage.getItem("lastMessageTimestamp");
    this.userId = null;
    this.markedAsReadMessages = new Set(); // Track messages already marked as read
  }

  /**
   * Connect to messaging socket namespace
   * Uses singleton pattern - prevents duplicate connections
   */
  connect() {
    // Already connected - return existing socket
    if (this.socket && this.isConnected) {
      return this.socket;
    }

    // Connection in progress - don't create another
    if (this.isConnecting) {
      return this.socket;
    }

    // Socket exists but disconnected - try to reconnect
    if (this.socket && !this.isConnected) {
      this.socket.connect();
      return this.socket;
    }

    this.isConnecting = true;

    try {
      const token = getAuthToken();
      if (!token) {
return null;
      }

      // Extract user ID from token for user room joining
      let userId = null;
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        userId = payload.id || payload.userId || payload._id;
      } catch (e) { /* ignored */ }

      this.userId = userId;

      // Validate API_BASE_URL first
      if (!API_BASE_URL) {
throw new Error("API_BASE_URL not configured");
      }

      // Extract base URL (remove trailing /api if present)
      let baseUrl = API_BASE_URL.endsWith("/api")
        ? API_BASE_URL.slice(0, -4)
        : API_BASE_URL;

      // Ensure we have a valid URL with protocol AFTER removing /api
      if (!baseUrl.startsWith("http://") && !baseUrl.startsWith("https://")) {
throw new Error("Invalid API URL configuration - missing protocol");
      }

      // Connect to /messaging namespace
      const fullSocketUrl = `${baseUrl}/messaging`;

      // Final validation: make sure URL doesn't have weird patterns
      if (
        fullSocketUrl.includes("https://https") ||
        fullSocketUrl.includes("http://http")
      ) {
throw new Error("Malformed Socket URL - double protocol detected");
      }

      this.socket = io(fullSocketUrl, {
        path: "/socket.io/",
        auth: {
          token: token,
        },
        transports: ["websocket", "polling"], // WebSocket first for better performance
        upgrade: true,
        withCredentials: true,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: this.maxReconnectAttempts,
        timeout: 20000, // Backend recommends 20 seconds
      });

      this.setupEventHandlers();
      this.isConnecting = false;

      return this.socket;
    } catch (error) {
this.isConnecting = false;
      return null;
    }
  }

  /**
   * Setup core socket event handlers
   */
  setupEventHandlers() {
    if (!this.socket) return;

    // Connection events
    this.socket.on("connect", () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;

      // CRITICAL: Join user room first - backend emits to user:userId rooms!
      if (this.userId) {
        this.socket.emit(
          "join_user_room",
          { userId: this.userId },
          (response) => {}
        );
      }

      // Start heartbeat interval (every 30 seconds)
      this.startHeartbeat();

      // Rejoin all active conversations after reconnection
      this.activeConversations.forEach((conversationId) => {
        this.joinConversation(conversationId);
      });

      // Sync missed messages after reconnect
      if (this.lastMessageTimestamp) {
        this.socket.emit("sync_messages", {
          lastMessageTimestamp: this.lastMessageTimestamp,
          conversationIds: Array.from(this.activeConversations),
        });
      }

      this.emit("socket_connected", { connected: true });
    });

    // Connection established event from backend
    this.socket.on("connection_established", (data) => {
      this.emit("connection_established", data);
    });

    // Online contacts list on connect
    this.socket.on("online_contacts_list", (data) => {
      this.emit("online_contacts_list", data);
    });

    this.socket.on("disconnect", (reason) => {
      this.isConnected = false;
      this.stopHeartbeat();
      this.emit("socket_disconnected", { reason });
    });

    this.socket.on("connect_error", (error) => {
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        this.emit("socket_error", {
          error: "Max reconnection attempts reached",
        });
      }
    });

    // Listen for transport upgrades
    this.socket.io.engine.on("upgrade", (transport) => {});

    // Message events
    this.socket.on("new_message", (data) => {
      // Update last message timestamp for sync
      if (data.message?.createdAt) {
        this.lastMessageTimestamp = data.message.createdAt;
        localStorage.setItem("lastMessageTimestamp", data.message.createdAt);
      }

      this.emit("new_message", data);
    });

    // Message sent confirmation (server confirms our message)
    this.socket.on("message_sent", (data) => {
      this.emit("message_sent", data);
    });

    // Message send error
    this.socket.on("message_error", (data) => {
      this.emit("message_error", data);
    });

    this.socket.on("message_delivered", (data) => {
      this.emit("message_delivered", data);
    });

    // Batch messages delivered
    this.socket.on("messages_delivered", (data) => {
      this.emit("messages_delivered", data);
    });

    this.socket.on("messages_read", (data) => {
      this.emit("messages_read", data);
    });

    // Sync complete event
    this.socket.on("sync_complete", (data) => {
      this.emit("sync_complete", data);
    });

    // Heartbeat acknowledgment
    this.socket.on("heartbeat_ack", (data) => {
      this.emit("heartbeat_ack", data);
    });

    // Server ping check
    this.socket.on("ping_check", (data) => {
      if (this.socket && this.isConnected) {
        this.socket.emit("pong_response", { timestamp: Date.now() });
      }
    });

    // Typing indicators
    this.socket.on("user_typing", (data) => {
      this.emit("user_typing", data);
    });

    this.socket.on("user_stopped_typing", (data) => {
      this.emit("user_stopped_typing", data);
    });

    // Online/Offline status events
    this.socket.on("user_online", (data) => {
      this.emit("user_online", data);
    });

    this.socket.on("user_offline", (data) => {
      this.emit("user_offline", data);
    });

    this.socket.on("user_presence_update", (data) => {
      this.emit("user_presence_update", data);
    });

    // Message action events (reactions, edits, deletes)
    this.socket.on("reaction_added", (data) => {
      this.emit("reaction_added", data);
    });

    this.socket.on("reaction_removed", (data) => {
      this.emit("reaction_removed", data);
    });

    this.socket.on("message_edited", (data) => {
      this.emit("message_edited", data);
    });

    this.socket.on("message_deleted", (data) => {
      this.emit("message_deleted", data);
    });

    // Conversation events
    this.socket.on("conversation_joined", (data) => {
      this.emit("conversation_joined", data);
    });

    this.socket.on("conversation_left", (data) => {
      this.emit("conversation_left", data);
    });

    // Error handling
    this.socket.on("error", (error) => {
this.emit("socket_error", error);
    });
  }

  /**
   * Join a conversation room for real-time updates
   *
   * @param {string} conversationId - Conversation ID to join
   */
  joinConversation(conversationId) {
    if (!this.socket || !this.isConnected) {
return;
    }

    this.socket.emit("join_conversation", { conversationId });
    this.activeConversations.add(conversationId);
  }

  /**
   * Leave a conversation room
   *
   * @param {string} conversationId - Conversation ID to leave
   */
  leaveConversation(conversationId) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit("leave_conversation", { conversationId });
    this.activeConversations.delete(conversationId);
  }

  /**
   * Send a message via socket for instant delivery
   *
   * @param {string} conversationId - Conversation ID
   * @param {string} content - Message content
   * @param {string} tempId - Temporary ID for optimistic update
   * @param {string} replyTo - Reply to message ID (optional)
   * @returns {string} tempId for tracking
   */
  sendMessage(
    conversationId,
    content,
    tempId = null,
    replyTo = null,
    messageType = "text"
  ) {
    if (!this.socket || !this.isConnected) {
return null;
    }

    const msgTempId =
      tempId || `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    this.socket.emit("send_message", {
      conversationId,
      content,
      tempId: msgTempId,
      replyTo,
      messageType, // Required by backend
    });

    return msgTempId;
  }

  /**
   * Mark message as read
   *
   * @param {string} conversationId - Conversation ID
   * @param {string} messageId - Message ID
   */
  markRead(conversationId, messageId) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit("mark_read", { conversationId, messageId });
  }

  /**
   * Batch mark messages as read
   * Includes deduplication to prevent marking same messages twice
   *
   * @param {string} conversationId - Conversation ID
   * @param {string[]} messageIds - Array of message IDs
   */
  batchMarkRead(conversationId, messageIds) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    // Filter out messages already marked as read
    const newMessageIds = messageIds.filter(
      (id) => !this.markedAsReadMessages.has(id)
    );

    if (newMessageIds.length === 0) {
      return;
    }

    // Add to tracked set
    newMessageIds.forEach((id) => this.markedAsReadMessages.add(id));

    this.socket.emit("batch_mark_read", {
      conversationId,
      messageIds: newMessageIds,
    });
  }

  /**
   * Clear marked as read tracking (call when switching conversations)
   */
  clearMarkedAsRead() {
    this.markedAsReadMessages.clear();
  }

  /**
   * Add reaction to a message via socket
   *
   * @param {string} messageId - Message ID
   * @param {string} emoji - Emoji reaction
   */
  addReaction(messageId, emoji) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit("add_reaction", { messageId, emoji });
  }

  /**
   * Remove reaction from a message via socket
   *
   * @param {string} messageId - Message ID
   * @param {string} emoji - Emoji reaction to remove
   */
  removeReaction(messageId, emoji) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit("remove_reaction", { messageId, emoji });
  }

  /**
   * Edit a message via socket
   *
   * @param {string} messageId - Message ID
   * @param {string} newContent - New message content
   */
  editMessage(messageId, newContent) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit("edit_message", { messageId, newContent });
  }

  /**
   * Delete a message via socket
   *
   * @param {string} messageId - Message ID
   */
  deleteMessage(messageId) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit("delete_message", { messageId });
  }

  /**
   * Update user status
   *
   * @param {string} status - Status: 'online', 'away', 'busy', 'invisible'
   * @param {string} statusMessage - Optional status message
   */
  updateStatus(status, statusMessage = null) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit("update_status", { status, statusMessage });
  }

  /**
   * Get online status for specific users
   *
   * @param {string[]} userIds - Array of user IDs
   */
  getOnlineUsers(userIds) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit("get_online_users", { userIds });
  }

  /**
   * Start heartbeat interval
   */
  startHeartbeat() {
    this.stopHeartbeat(); // Clear any existing interval

    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.isConnected) {
        this.socket.emit("heartbeat", { timestamp: Date.now() });
      }
    }, 30000);
  }

  /**
   * Stop heartbeat interval
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Sync messages after reconnection
   *
   * @param {string} lastTimestamp - Last message timestamp
   * @param {string[]} conversationIds - Array of conversation IDs to sync
   */
  syncMessages(lastTimestamp, conversationIds = []) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit("sync_messages", {
      lastMessageTimestamp: lastTimestamp,
      conversationIds,
    });
  }

  /**
   * Send typing start indicator
   *
   * @param {string} conversationId - Conversation ID
   */
  sendTypingStart(conversationId) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit("typing_start", { conversationId });
  }

  /**
   * Send typing stop indicator
   *
   * @param {string} conversationId - Conversation ID
   */
  sendTypingStop(conversationId) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit("typing_stop", { conversationId });
  }

  /**
   * Register an event listener
   *
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  /**
   * Unregister an event listener
   *
   * @param {string} event - Event name
   * @param {Function} callback - Callback function to remove
   */
  off(event, callback) {
    if (!this.listeners.has(event)) {
      return;
    }

    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);

    if (index > -1) {
      callbacks.splice(index, 1);
    }

    if (callbacks.length === 0) {
      this.listeners.delete(event);
    }
  }

  /**
   * Emit an event to all registered listeners
   *
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    if (!this.listeners.has(event)) {
      return;
    }

    const callbacks = this.listeners.get(event);
    callbacks.forEach((callback) => {
      try {
        callback(data);
      } catch (error) { /* ignored */ }
    });
  }

  /**
   * Disconnect from messaging socket
   */
  disconnect() {
    if (this.socket) {
      // Stop heartbeat
      this.stopHeartbeat();

      // Leave all active conversations
      this.activeConversations.forEach((conversationId) => {
        this.leaveConversation(conversationId);
      });

      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.activeConversations.clear();
    }
  }

  /**
   * Check if socket is connected
   *
   * @returns {boolean} Connection status
   */
  isSocketConnected() {
    return this.isConnected && this.socket !== null;
  }

  /**
   * Get active conversations
   *
   * @returns {Set} Set of active conversation IDs
   */
  getActiveConversations() {
    return new Set(this.activeConversations);
  }

  /**
   * Get user ID
   *
   * @returns {string} User ID
   */
  getUserId() {
    return this.userId;
  }

  /**
   * Get last message timestamp
   *
   * @returns {string} Last message timestamp
   */
  getLastMessageTimestamp() {
    return this.lastMessageTimestamp;
  }

  /**
   * Set last message timestamp
   *
   * @param {string} timestamp - Timestamp
   */
  setLastMessageTimestamp(timestamp) {
    this.lastMessageTimestamp = timestamp;
    localStorage.setItem("lastMessageTimestamp", timestamp);
  }

  /**
   * Clear all event listeners
   */
  clearListeners() {
    this.listeners.clear();
  }
}

// Create and export singleton instance
const messagingSocketService = new MessagingSocketService();
export default messagingSocketService;
